import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from "react";
import { InterviewContext } from "../../../contexts/InterviewContext";
import { debounce } from "lodash";
import { useLoading } from "src/contexts/LoadingContext";

// Define tutorial steps with more detailed properties
export enum TutorialStep {
    WELCOME = "welcome",
    TIMELINE = "timeline",
    INTERVIEWER = "interviewer",
    CAMERA = "camera",
    GRAPHS = "graphs",
    SKIP_QUESTION = "skip_question",
    MICROPHONE = "microphone",
    MICROPHONE_INTERACTIVE = "microphone_interactive", // Interactive step
    COMPLETE = "complete",
}

interface TutorialStepConfig {
    audio: string; // Audio script to read
    requiresAction?: boolean; // Does this step require user interaction?
    targetAction?: string; // What action must the user perform? (e.g., "microphone")
    waitForAudioComplete?: boolean; // Should we wait for audio to complete before allowing next step?
}

interface TutorialContextType {
    isActive: boolean;
    currentStep: TutorialStep | null;
    startTutorial: () => void;
    endTutorial: () => void;
    nextStep: () => void;
    prevStep: () => void;
    goToStep: (step: TutorialStep) => void;
    hasSeenTutorial: boolean;
    setHasSeenTutorial: (value: boolean) => void;
    isAudioPlaying: boolean;
    skipAudio: () => void;
    isAwaitingAction: boolean;
    completeAction: (action: string) => void;
    canProceed: boolean;
}

// Define props for the provider including isFirstTimeUser
interface TutorialProviderProps {
    children: ReactNode;
    isFirstTimeUser?: boolean;
}

const TutorialContext = createContext<TutorialContextType | undefined>(
    undefined
);

// Tutorial step configurations
const tutorialStepConfigs: Record<TutorialStep, TutorialStepConfig> = {
    [TutorialStep.WELCOME]: {
        audio: "Welcome to your mock case interview tutorial! I'll guide you through the different elements of the interface to help you get comfortable. You can press next at any time to continue, or skip my audio instructions using the skip button.",
        waitForAudioComplete: false,
    },
    [TutorialStep.TIMELINE]: {
        audio: "This timeline shows your progress through the case interview. Each node represents a different question or section. The highlighted node is your current position.",
        waitForAudioComplete: false,
    },
    [TutorialStep.INTERVIEWER]: {
        audio: "I'm your We Kruit interviewer. The avatar will pulse purple when I'm speaking to you. When it's your turn to speak, the pulsing will stop.",
        waitForAudioComplete: false,
    },
    [TutorialStep.CAMERA]: {
        audio: "This button allows you to turn your camera on or off. Having your camera on provides a more realistic interview experience.",
        waitForAudioComplete: false,
    },
    [TutorialStep.MICROPHONE]: {
        audio: "This microphone button activates when it's your turn to speak. When I finish asking a question, click this to begin your response.",
        waitForAudioComplete: false,
    },
    [TutorialStep.MICROPHONE_INTERACTIVE]: {
        audio: "Let's try an actual interaction. I'll ask you a simple question. After I finish speaking, go ahead and try to answer it. After finish talking, click the microphone button. Now, what's your dream company?",
        requiresAction: true,
        targetAction: "microphone",
        waitForAudioComplete: true,
    },
    [TutorialStep.SKIP_QUESTION]: {
        audio: "Perfect! That's how you'll interact with me during the interview. Let's continue with the tutorial. If you want to skip a question, you can use this button. This will move you to the next question in the interview.",
        waitForAudioComplete: false,
    },
    [TutorialStep.GRAPHS]: {
        audio: "During the interview, I may display graphs, charts, or other visual information relevant to the case. You can navigate between multiple graphs using the arrow buttons if they appear.",
        waitForAudioComplete: false,
    },
    [TutorialStep.COMPLETE]: {
        audio: "Great! You now understand all the key elements of the mock interview interface. The interview will begin with an introduction. Good luck with your case interview!",
        waitForAudioComplete: true,
    },
};

// Order of tutorial steps
const tutorialSteps: TutorialStep[] = [
    TutorialStep.WELCOME,
    TutorialStep.TIMELINE,
    TutorialStep.INTERVIEWER,
    TutorialStep.CAMERA,
    TutorialStep.MICROPHONE,
    TutorialStep.MICROPHONE_INTERACTIVE,
    TutorialStep.GRAPHS,
    TutorialStep.SKIP_QUESTION,
    TutorialStep.COMPLETE,
];

export const TutorialProvider: React.FC<TutorialProviderProps> = ({
    children,
    isFirstTimeUser = false,
}) => {
    // Get InterviewContext for audio control
    const interviewContext = useContext(InterviewContext);

    if (!interviewContext) {
        throw new Error(
            "TutorialProvider must be used within an InterviewProvider"
        );
    }

    // Local state
    const [isActive, setIsActive] = useState<boolean>(false);
    const [currentStep, setCurrentStep] = useState<TutorialStep | null>(null);
    const [hasSeenTutorial, setHasSeenTutorial] = useState<boolean>(() => {
        return localStorage.getItem("hasSeenMockCaseTutorial") === "true";
    });
    const [isAudioPlaying, setIsAudioPlaying] = useState<boolean>(false);
    const [isAwaitingAction, setIsAwaitingAction] = useState<boolean>(false);
    const [canProceed, setCanProceed] = useState<boolean>(true);

    // Refs to track state across renders
    const stepInitializedRef = useRef<boolean>(false);
    const wasInterviewerSpeakingRef = useRef<boolean>(false);
    const havePlayedAudioForCurrentStepRef = useRef<boolean>(false);
    const completedActionsRef = useRef<Set<string>>(new Set());
    const stepTransitionInProgressRef = useRef<boolean>(false);
    const canProceedRef = useRef<boolean>(true); // Ref to track canProceed state synchronously
    const { handleTimeBasedLoading } = useLoading();

    // Function to update both canProceed state and ref
    const updateCanProceed = (value: boolean) => {
        setCanProceed(value);
        canProceedRef.current = value;
    };

    // De-structure needed functions from InterviewContext
    const { isInterviewerSpeaking, playAudioByContent, stopAudio } =
        interviewContext;

    // Only initialize the tutorial when the component mounts
    useEffect(() => {
        // Initialize the tutorial only for first-time users, with a slight delay
        console.log("isFirstTimeUser", isFirstTimeUser);
        if (isFirstTimeUser) {
            const timer = setTimeout(() => {
                setIsActive(true);
                setCurrentStep(TutorialStep.WELCOME);
            }, 500);

            // Clean up timer on unmount
            return () => clearTimeout(timer);
        } else {
            // Make sure tutorial is definitely not active for non-first-time users
            setIsActive(false);
            setCurrentStep(null);
        }
    }, [isFirstTimeUser]);

    // Handle audio playback when step changes
    useEffect(() => {
        console.log("[Tutorial] Step change effect triggered", {
            isActive,
            currentStep,
        });

        // Only proceed if we're active and have a current step
        if (!isActive || !currentStep) {
            console.log(
                "[Tutorial] Skipping audio - tutorial not active or no current step"
            );
            stepInitializedRef.current = false;
            havePlayedAudioForCurrentStepRef.current = false;
            return;
        }

        // Prevent double initialization
        if (stepInitializedRef.current) {
            console.log("[Tutorial] Skipping audio - step already initialized");
            return;
        }

        console.log("[Tutorial] Initializing step:", currentStep);

        // Mark this step as initialized
        stepInitializedRef.current = true;

        // Reset step-specific state
        setIsAwaitingAction(false);
        completedActionsRef.current.clear();
        havePlayedAudioForCurrentStepRef.current = false;

        // Function to play audio with proper checks
        const playStepAudio = async () => {
            console.log("[Tutorial] Attempting to play step audio");

            // Ensure we haven't already played audio for this step to prevent duplicates
            if (havePlayedAudioForCurrentStepRef.current) {
                console.log(
                    "[Tutorial] Skipping audio - already played for this step"
                );
                return;
            }

            // Mark that we've played audio for this step
            havePlayedAudioForCurrentStepRef.current = true;

            // Get the audio script for this step
            const stepConfig = tutorialStepConfigs[currentStep];
            console.log(
                "[Tutorial] Playing audio for step config:",
                stepConfig
            );

            try {
                // Set initial states for this step's audio
                const shouldProceed = !stepConfig.waitForAudioComplete;
                updateCanProceed(shouldProceed);
                if (stepConfig.requiresAction) {
                    setIsAwaitingAction(true);
                    updateCanProceed(false);
                } else {
                    updateCanProceed(true);
                }

                console.log(
                    "[Tutorial] About to play audio content:",
                    stepConfig.audio
                );
                // Play the audio via InterviewContext
                await playAudioByContent(stepConfig.audio);
                console.log("[Tutorial] Audio playback initiated successfully");
            } catch (error) {
                console.error(
                    "[Tutorial] Error playing tutorial audio:",
                    error
                );
                setIsAudioPlaying(false);

                if (stepConfig.requiresAction) {
                    setIsAwaitingAction(true);
                    updateCanProceed(false);
                } else {
                    updateCanProceed(true);
                }
            }
        };

        // Set a small delay before playing audio to ensure transition is complete
        console.log("[Tutorial] Setting up audio delay timer");
        const audioDelay = setTimeout(() => {
            console.log(
                "[Tutorial] Audio delay timer fired, preparing to play audio"
            );
            stepTransitionInProgressRef.current = false;
            playStepAudio();
        }, 500); // Increased delay to ensure previous audio has fully stopped

        return () => {
            console.log("[Tutorial] Cleaning up audio delay timer");
            clearTimeout(audioDelay);
        };
    }, [isActive, currentStep, playAudioByContent]);

    // Monitor interviewer speaking state to detect audio completion
    useEffect(() => {
        console.log("[Tutorial] Interviewer speaking state changed:", {
            isInterviewerSpeaking,
            wasInterviewerSpeaking: wasInterviewerSpeakingRef.current,
        });

        // Track when interviewer starts speaking
        if (isInterviewerSpeaking && !wasInterviewerSpeakingRef.current) {
            console.log("[Tutorial] Interviewer started speaking");
            wasInterviewerSpeakingRef.current = true;
            setIsAudioPlaying(true);
        }

        // Track when interviewer stops speaking (audio ended)
        if (!isInterviewerSpeaking && wasInterviewerSpeakingRef.current) {
            console.log("[Tutorial] Interviewer stopped speaking");
            wasInterviewerSpeakingRef.current = false;
            setIsAudioPlaying(false);

            // Handle step-specific logic after audio completes
            if (currentStep) {
                const stepConfig = tutorialStepConfigs[currentStep];
                console.log("[Tutorial] Handling audio completion for step:", {
                    currentStep,
                    requiresAction: stepConfig.requiresAction,
                });

                if (stepConfig.requiresAction) {
                    setIsAwaitingAction(true);
                    updateCanProceed(false);
                } else {
                    updateCanProceed(true);
                }
            }
        }
    }, [isInterviewerSpeaking, currentStep]);

    // Auto-start tutorial for first-time users
    useEffect(() => {
        if (isFirstTimeUser && !hasSeenTutorial && !isActive) {
            const timer = setTimeout(() => {
                startTutorial();
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [isFirstTimeUser, hasSeenTutorial, isActive]);

    // Clean up on component unmount
    useEffect(() => {
        return () => {
            // If tutorial is active when component unmounts, stop any audio
            if (isActive && isInterviewerSpeaking) {
                stopAudio();
            }
        };
    }, [isActive, isInterviewerSpeaking, stopAudio]);

    // Start the tutorial
    const startTutorial = async () => {
        // Reset all state
        await new Promise((resolve) => setTimeout(resolve, 600));
        havePlayedAudioForCurrentStepRef.current = false;
        stepInitializedRef.current = false;
        wasInterviewerSpeakingRef.current = false;
        stepTransitionInProgressRef.current = false;
        canProceedRef.current = true; // Reset the ref

        setIsActive(true);
        setCurrentStep(TutorialStep.WELCOME);
    };

    // End the tutorial
    const endTutorial = async () => {
        // Stop any active audio
        if (isInterviewerSpeaking) {
            stopAudio();
        }

        // Reset all state
        stepInitializedRef.current = false;
        havePlayedAudioForCurrentStepRef.current = false;
        wasInterviewerSpeakingRef.current = false;
        stepTransitionInProgressRef.current = false;

        setIsActive(false);
        setCurrentStep(null);
        setIsAudioPlaying(false);
        setIsAwaitingAction(false);
        updateCanProceed(true); // Updated to use the helper function

        // Save tutorial completion
        localStorage.setItem("hasSeenMockCaseTutorial", "true");
        setHasSeenTutorial(true);

        const lastCaseInterview = localStorage.getItem("lastCaseInterview");
        // Convert string to number or use default value 0 if null
        const caseId = lastCaseInterview ? parseInt(lastCaseInterview) : 101;
        await handleStartMockInterview(caseId);
    };

    const handleStartMockInterview = async (caseId: number) => {
        console.log("Starting interview for case:", caseId);

        try {
            interviewContext.resetInterview();
            const didStart = await handleTimeBasedLoading(
                interviewContext.startInterviewHandler(
                    "",
                    caseId,
                    true,
                    false,
                    ""
                ),
                // new Promise((resolve) => setTimeout(resolve, 5000)),
                {
                    totalDuration: 3500,
                    messages: [
                        "Initializing your mock interview...",
                        "Setting up your case materials...",
                        "Preparing interview environment...",
                        "Almost ready to begin...",
                    ],
                    onComplete: () => {
                        console.log("Loading complete");
                    },
                }
            );
            console.log("Did start interview:", didStart);
        } catch (err) {
            console.error("Error starting interview:", err);
        }
    };

    // Skip current audio
    const skipAudio = () => {
        if (isInterviewerSpeaking) {
            stopAudio();

            // Update local state
            wasInterviewerSpeakingRef.current = false;
            setIsAudioPlaying(false);

            // Determine next state based on current step
            if (currentStep) {
                const stepConfig = tutorialStepConfigs[currentStep];

                if (stepConfig.requiresAction) {
                    setIsAwaitingAction(true);
                    updateCanProceed(false); // Updated to use the helper function
                } else {
                    updateCanProceed(true); // Updated to use the helper function
                }
            }
        }
    };

    // Complete a required action
    const completeAction = (action: string) => {
        if (!currentStep) return;

        const stepConfig = tutorialStepConfigs[currentStep];
        if (stepConfig.requiresAction && stepConfig.targetAction === action) {
            completedActionsRef.current.add(action);
            setIsAwaitingAction(false);

            // Update both state and ref for synchronous access
            updateCanProceed(true); // Updated to use the helper function

            // For microphone interactive step, play a response
            if (
                currentStep === TutorialStep.MICROPHONE_INTERACTIVE &&
                !isInterviewerSpeaking
            ) {
                // Small delay to feel more natural
                setTimeout(async () => {
                    // Reset for new audio
                    havePlayedAudioForCurrentStepRef.current = false;
                    nextStep();
                }, 700);
            }
        }
    };

    // Update handleNextStep to use the ref
    const handleNextStep = useCallback(() => {
        // Check the ref value instead of the state
        if (
            !currentStep ||
            !canProceedRef.current ||
            stepTransitionInProgressRef.current
        )
            return;

        const currentIndex = tutorialSteps.indexOf(currentStep);
        console.log("currentIndex", currentIndex);
        console.log("tutorialSteps.length", tutorialSteps.length);
        if (currentIndex < tutorialSteps.length - 1) {
            // Mark transition in progress to prevent multiple navigations
            stepTransitionInProgressRef.current = true;

            // Also update canProceed ref to prevent multiple transitions
            canProceedRef.current = false;

            // Stop any active audio
            if (isInterviewerSpeaking) {
                // Stop the audio directly without using skipAudio to avoid state conflicts
                stopAudio();

                // Add a small delay to ensure audio system has time to reset
                setTimeout(() => {
                    // Reset step state
                    stepInitializedRef.current = false;
                    havePlayedAudioForCurrentStepRef.current = false;
                    wasInterviewerSpeakingRef.current = false;
                    setIsAudioPlaying(false);

                    // Navigate to next step
                    setCurrentStep(tutorialSteps[currentIndex + 1]);
                }, 200);
            } else {
                // Reset step state
                stepInitializedRef.current = false;
                havePlayedAudioForCurrentStepRef.current = false;
                wasInterviewerSpeakingRef.current = false;

                // Navigate to next step
                setCurrentStep(tutorialSteps[currentIndex + 1]);
            }
        } else {
            endTutorial();
        }
    }, [
        currentStep,
        canProceed,
        tutorialSteps,
        isInterviewerSpeaking,
        stopAudio,
        setIsAudioPlaying,
        setCurrentStep,
        endTutorial,
    ]);

    // Wrap the prevStep logic in useCallback for referential stability
    const handlePrevStep = useCallback(() => {
        if (!currentStep || stepTransitionInProgressRef.current) return;

        const currentIndex = tutorialSteps.indexOf(currentStep);
        if (currentIndex > 0) {
            // Stop any active audio
            if (isInterviewerSpeaking) {
                stopAudio();
            }

            // Mark transition in progress
            stepTransitionInProgressRef.current = true;

            // Reset step state
            stepInitializedRef.current = false;
            havePlayedAudioForCurrentStepRef.current = false;
            wasInterviewerSpeakingRef.current = false;

            // Navigate to previous step
            setCurrentStep(tutorialSteps[currentIndex - 1]);
        }
    }, [
        currentStep,
        tutorialSteps,
        isInterviewerSpeaking,
        stopAudio,
        setCurrentStep,
    ]);

    // Create debounced versions of the functions with useMemo
    const nextStep = useMemo(
        () => debounce(handleNextStep, 500),
        [handleNextStep]
    );

    const prevStep = useMemo(
        () => debounce(handlePrevStep, 500),
        [handlePrevStep]
    );

    // Go to a specific step
    const goToStep = (step: TutorialStep) => {
        if (
            !tutorialSteps.includes(step) ||
            stepTransitionInProgressRef.current
        )
            return;

        // Stop any active audio
        if (isInterviewerSpeaking) {
            stopAudio();
        }

        // Mark transition in progress
        stepTransitionInProgressRef.current = true;

        // Reset step state
        stepInitializedRef.current = false;
        havePlayedAudioForCurrentStepRef.current = false;
        wasInterviewerSpeakingRef.current = false;

        // Navigate to selected step
        setCurrentStep(step);
        setIsActive(true);
    };

    return (
        <TutorialContext.Provider
            value={{
                isActive,
                currentStep,
                startTutorial,
                endTutorial,
                nextStep,
                prevStep,
                goToStep,
                hasSeenTutorial,
                setHasSeenTutorial,
                isAudioPlaying,
                skipAudio,
                isAwaitingAction,
                completeAction,
                canProceed,
            }}>
            {children}
        </TutorialContext.Provider>
    );
};

export const useTutorial = () => {
    const context = useContext(TutorialContext);
    if (context === undefined) {
        throw new Error("useTutorial must be used within a TutorialProvider");
    }
    return context;
};
